import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Stack,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Alert,
  ActionIcon,
  Flex,
  Card,
  Badge,
  Menu,
  Pagination,
  Box,
  rem,
  SimpleGrid,
  Loader,
  Skeleton,
  Select,
  Tooltip,
  Collapse,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useFeatureFlag } from '../../hooks/useFeatureFlags';
import {
  IconClock,
  IconUsers,
  IconChevronLeft,
  IconDotsVertical,
  IconEdit,
  IconAlertCircle,
  IconBookmark,
  IconBookmarkOff,
  IconCheck,
  IconPhoto,
  IconTrash,
  IconPlus,
  IconArchive,
  IconArrowUp,
  IconArrowDown,
  IconWand,
  IconX
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../hooks/useAuth';
import { RecipeProvenanceBadges } from '../Recipe/RecipeProvenanceBadges';
import { useAppUser } from '../../hooks/useAppUser';
import { Recipe } from '../../types/mealPlan';
import { MobileOptimized } from '../Layout/MobileOptimized';
import { formatTime } from '../../utils/formatTime';
import { ImportRecipeModal } from '../Recipe/RecipeImport';
import { FailedImportsDrawer } from '../Recipe/FailedImportsDrawer';
import { SplitButton } from '../UI/SplitButton';
import { RecipeSearchFilters } from '../Recipe/RecipeSearchFilters';
import { MEAL_SLOT_MATCHERS } from '../../utils/mealSlotMatchers';
import { RecipeImage } from '../UI/RecipeImage';
import { saveCookbookState, loadCookbookState } from '../../utils/persistentState';
import { getPageCache, setPageCache } from '../../utils/pageCache';
import { SUMMARY_VIEW_PARAM } from '../../utils/recipeSummary';
import { buildRecipePath } from '../../utils/routeHelpers';
import { getRecipeBadges, splitBadgesForDisplay, RecipeBadgeViewer } from '../../utils/recipeBadges';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { householdSegment } from '../../utils/householdSegment';
import { collection, query, where, QuerySnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger, FirestoreTriggerSnapshot } from '../../hooks/useFirestoreTrigger';
import { CuisineBadge } from '../UI/CuisineBadge';
import { formatCount } from '../../utils/formatCount';
import {
  describeNewlyAdded,
  findNewlyAddedRecipeIds,
  isCookbookDisplayable,
  mergeNewlyAddedIds,
  pinRecipesToTop,
} from '../../utils/newlyAddedRecipes';

interface MyCookbookProps {
  onBack: () => void;
}

type CookbookCacheEntry = {
  recipes: Recipe[];
  importCounts: { pending: number; processing: number; failed: number };
  remixCounts: { pending: number; processing: number; failed: number };
  newlyAddedIds?: string[];
};

export const MyCookbook = ({ onBack }: MyCookbookProps) => {
  const { user } = useAuth();
  const { user: appUser } = useAppUser();
  const effectiveUid = appUser?.uid || user?.uid;
  const badgeViewer: RecipeBadgeViewer = {
    dietaryRestrictions: appUser?.familyProfile?.dietaryRestrictions,
    householdSegment: householdSegment(appUser?.familyProfile),
  };
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  // Seed from the cookbook this session last displayed, so a back navigation
  // renders recipes on the first paint; the mount fetch then refreshes them
  // silently instead of flashing the loading skeletons.
  const cookbookCacheKey = effectiveUid ? `cookbook:${effectiveUid}` : null;
  const seededCookbook = getPageCache<CookbookCacheEntry>(cookbookCacheKey);
  const [recipes, setRecipes] = useState<Recipe[]>(seededCookbook?.recipes ?? []);
  const [importCounts, setImportCounts] = useState<{
    pending: number;
    processing: number;
    failed: number;
  }>(seededCookbook?.importCounts ?? { pending: 0, processing: 0, failed: 0 });
  const [remixCounts, setRemixCounts] = useState<{
    pending: number;
    processing: number;
    failed: number;
  }>(seededCookbook?.remixCounts ?? { pending: 0, processing: 0, failed: 0 });
  // Skeletons until the first fetch when there's no seed — starting false
  // painted the empty state ("You haven't saved any recipes") first
  const [loading, setLoading] = useState(!seededCookbook);
  const [error, setError] = useState<string | null>(null);
  // Latest-wins ordering for list fetches: a refetch from an import landing
  // can race one from an undo or a remix starting, and a superseded response
  // must not overwrite newer state (or the session cache mirrored from it).
  const latestFetchRef = useRef(0);
  const firstRecipeRef = useRef<HTMLDivElement>(null);
  const hasInitiallyLoadedRef = useRef(false);
  // Whether real data is on screen (seeded or fetched) — gates the cache
  // mirror below so an empty pre-fetch state is never cached.
  const hasCookbookDataRef = useRef(!!seededCookbook);
  // Imports and remixes that finished while the cookbook was watching,
  // newest first. Pinned to the top of the list (past sort, filters, and
  // pagination) until dismissed, so they never land somewhere hard to find.
  const [newlyAddedIds, setNewlyAddedIds] = useState<string[]>(
    seededCookbook?.newlyAddedIds ?? []
  );
  // Every recipe id this cookbook has displayed (in-flight remixes aren't
  // displayed) — the baseline that tells a just-finished import or remix
  // apart from an existing recipe. null until the first real recipe list,
  // so the initial load doesn't flag everything as new.
  const shownRecipeIdsRef = useRef<Set<string> | null>(
    seededCookbook
      ? new Set(seededCookbook.recipes.filter(isCookbookDisplayable).map((r) => r.id))
      : null
  );
  
  // Initialize state from localStorage or defaults
  const initializeState = () => {
    const savedState = loadCookbookState();
    return {
      searchQuery: savedState?.searchQuery ?? '',
      selectedCuisines: savedState?.selectedCuisines ?? [],
      selectedDifficulty: savedState?.selectedDifficulty ?? '',
      selectedDietaryRestrictions: savedState?.selectedDietaryRestrictions ?? [],
      selectedPrepTime: savedState?.selectedPrepTime ?? '',
      selectedTotalTime: savedState?.selectedTotalTime ?? '',
      showNeedsReviewOnly: savedState?.showNeedsReviewOnly ?? false,
      selectedOrigin: savedState?.selectedOrigin ?? '',
      selectedMeal: savedState?.selectedMeal ?? '',
      sortField: savedState?.sortField ?? 'name',
      sortDirection: savedState?.sortDirection ?? 'asc' as 'asc' | 'desc',
      currentPage: savedState?.currentPage ?? 1,
    };
  };

  const initialState = initializeState();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>(initialState.selectedCuisines);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>(initialState.selectedDifficulty);
  const [selectedDietaryRestrictions, setSelectedDietaryRestrictions] = useState<string[]>(initialState.selectedDietaryRestrictions);
  const [selectedPrepTime, setSelectedPrepTime] = useState<string>(initialState.selectedPrepTime);
  const [selectedTotalTime, setSelectedTotalTime] = useState<string>(initialState.selectedTotalTime);
  const [showNeedsReviewOnly, setShowNeedsReviewOnly] = useState(initialState.showNeedsReviewOnly);
  const [selectedOrigin, setSelectedOrigin] = useState<string>(initialState.selectedOrigin);
  const [selectedMeal, setSelectedMeal] = useState<string>(initialState.selectedMeal);
  // The Meal filter only exists behind the slots flag; without it the
  // handler stays unset and RecipeSearchFilters hides the control
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  
  // Sorting state
  const [sortField, setSortField] = useState<string>(initialState.sortField);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialState.sortDirection);
  
  // Track recipes that have been optimistically removed
  const [optimisticallyRemovedRecipes, setOptimisticallyRemovedRecipes] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const recipesPerPage = 12;

  // Import modal state
  const [importModalOpened, setImportModalOpened] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('pinterest') === 'connected';
  });

  // Failed imports modal state
  const [failedImportsModalOpened, setFailedImportsModalOpened] = useState(false);
  const [failedRecipes, setFailedRecipes] = useState<Recipe[]>([]);
  
  // Remix functionality state
  const [remixMode, setRemixMode] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState<Set<string>>(new Set());
  const [isRemixing, setIsRemixing] = useState(false);
  
  const fetchUserRecipes = useCallback(async (showLoading: boolean = true) => {
    if (!user) return;
    
    if (showLoading) {
      setLoading(true);
      setError(null);
    }
    const fetchId = ++latestFetchRef.current;
    const isLatest = () => fetchId === latestFetchRef.current;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes?${SUMMARY_VIEW_PARAM}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }

      const data = await response.json();
      if (!isLatest()) return;
      const newRecipes = data.recipes || [];
      
      const shownIds = shownRecipeIdsRef.current;
      if (shownIds) {
        const landed = findNewlyAddedRecipeIds(newRecipes, shownIds);
        if (landed.length > 0) {
          setNewlyAddedIds(prev => mergeNewlyAddedIds(prev, landed));
        }
      }
      const nextShownIds = new Set(shownIds);
      newRecipes
        .filter(isCookbookDisplayable)
        .forEach((r: Recipe) => nextShownIds.add(r.id));
      shownRecipeIdsRef.current = nextShownIds;

      hasCookbookDataRef.current = true;
      setRecipes(newRecipes);
      const newImportCounts = data.importCounts || { pending: 0, processing: 0, failed: 0 };
      setImportCounts(newImportCounts);

      // Count remix statuses
      const newRemixCounts = { pending: 0, processing: 0, failed: 0 };
      newRecipes.forEach((recipe: Recipe) => {
        if (recipe.remixStatus?.status === "pending") {
          newRemixCounts.pending++;
        } else if (recipe.remixStatus?.status === "processing") {
          newRemixCounts.processing++;
        } else if (recipe.remixStatus?.status === "failed") {
          newRemixCounts.failed++;
        }
      });
      setRemixCounts(newRemixCounts);
    } catch (error: any) {
      console.error('Error fetching recipes:', error);
      // Only when nothing is on screen — a failed background refresh keeps
      // the seeded or fetched list
      if (isLatest() && !hasCookbookDataRef.current) {
        setError(error.message);
      }
    } finally {
      // The latest fetch settles loading even when it was a silent one that
      // superseded the initial (skeleton) load
      if (isLatest()) {
        setLoading(false);
      }
    }
  }, [user]);

  // Coalesced background refresh for change signals (Firestore triggers):
  // while one refresh is in flight, further signals queue a single trailing
  // refetch instead of stacking full-list requests.
  const refreshInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const requestRefresh = useCallback(async () => {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }
    refreshInFlightRef.current = true;
    try {
      do {
        refreshQueuedRef.current = false;
        await fetchUserRecipes(false);
      } while (refreshQueuedRef.current);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [fetchUserRecipes]);

  // An in-flight import/remix query changes membership when work starts or
  // finishes — the only changes the list cares about. Writes to a doc that
  // stays in flight (progress updates) arrive as 'modified' and are skipped.
  const handleInFlightTrigger = useCallback(
    (snapshot: FirestoreTriggerSnapshot) => {
      if (
        snapshot instanceof QuerySnapshot &&
        snapshot.docChanges().every((change) => change.type === 'modified')
      ) {
        return;
      }
      requestRefresh();
    },
    [requestRefresh]
  );

  const removeRecipeFromCookbook = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove recipe');
      }

      return true;
    } catch (error: any) {
      console.error('Error removing recipe:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  const addRecipeToCookbook = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to add recipe to cookbook');
      }

      return true;
    } catch (error: any) {
      console.error('Error adding recipe to cookbook:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  const deleteRecipe = useCallback(async (recipeId: string) => {
    if (!user) return false;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete recipe');
      }

      return true;
    } catch (error: any) {
      console.error('Error deleting recipe:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  useEffect(() => {
    // With seeded content this is a silent background refresh.
    fetchUserRecipes(!hasCookbookDataRef.current);
  }, [fetchUserRecipes]);

  // Mirror displayed data into the session cache. Runs on every recipes
  // change, so local mutations (optimistic removes, undo restores) are what a
  // back navigation resumes from, not the pre-mutation fetch.
  useEffect(() => {
    if (hasCookbookDataRef.current) {
      setPageCache(cookbookCacheKey, { recipes, importCounts, remixCounts, newlyAddedIds });
    }
  }, [recipes, importCounts, remixCounts, newlyAddedIds, cookbookCacheKey]);

  // Realtime refresh while there are active imports or remixes: watch the
  // user's in-flight recipe docs and refetch the cookbook whenever one
  // changes or reaches a terminal status (i.e. leaves the query).
  const hasActiveImports = importCounts.pending > 0 || importCounts.processing > 0;
  const hasActiveRemixes = remixCounts.pending > 0 || remixCounts.processing > 0;

  // The notification banners' Collapses animate closed after the counts
  // reach 0; keep the last non-zero totals so the exiting banners don't
  // flash "0 recipes are importing" etc.
  const lastImportTotalRef = useRef(1);
  const lastRemixTotalRef = useRef(1);
  const lastFailedCountRef = useRef(1);
  useEffect(() => {
    const importTotal = importCounts.pending + importCounts.processing;
    if (importTotal > 0) {
      lastImportTotalRef.current = importTotal;
    }
    if (importCounts.failed > 0) {
      lastFailedCountRef.current = importCounts.failed;
    }
  }, [importCounts]);
  useEffect(() => {
    const remixTotal = remixCounts.pending + remixCounts.processing;
    if (remixTotal > 0) {
      lastRemixTotalRef.current = remixTotal;
    }
  }, [remixCounts]);
  const displayFailedCount = importCounts.failed || lastFailedCountRef.current;
  const inFlightImportsQuery = useMemo(
    () =>
      effectiveUid
        ? query(
            collection(db, 'users', effectiveUid, 'recipes'),
            where('importStatus.status', 'in', ['pending', 'processing'])
          )
        : null,
    [effectiveUid]
  );
  const inFlightRemixesQuery = useMemo(
    () =>
      effectiveUid
        ? query(
            collection(db, 'users', effectiveUid, 'recipes'),
            where('remixStatus.status', 'in', ['pending', 'processing'])
          )
        : null,
    [effectiveUid]
  );
  useFirestoreTrigger({
    enabled: hasActiveImports,
    target: inFlightImportsQuery,
    onTrigger: handleInFlightTrigger,
  });
  useFirestoreTrigger({
    enabled: hasActiveRemixes,
    target: inFlightRemixesQuery,
    onTrigger: handleInFlightTrigger,
  });

  // Suppress ResizeObserver errors - this is a common, harmless browser issue
  // Reference: https://trackjs.com/javascript-errors/resizeobserver-loop-completed-with-undelivered-notifications/
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };
    
    window.addEventListener('error', handleError);
    
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Get unique values for filter options
  const cuisineOptions = useMemo(() => {
    const cuisines = new Set(recipes.map(r => r.cuisine).filter(Boolean) as string[]);
    return Array.from(cuisines).sort();
  }, [recipes]);

  const dietaryOptions = useMemo(() => {
    const restrictions = new Set<string>();
    recipes.forEach(recipe => {
      recipe.dietaryRestrictions?.forEach(restriction => restrictions.add(restriction));
    });
    return Array.from(restrictions).sort();
  }, [recipes]);

  // Just-added recipes still in the cookbook (deleted ones drop out)
  const visibleNewlyAddedIds = useMemo(() => {
    const present = new Set(
      recipes.filter(r => !optimisticallyRemovedRecipes.has(r.id)).map(r => r.id)
    );
    return newlyAddedIds.filter(id => present.has(id));
  }, [recipes, newlyAddedIds, optimisticallyRemovedRecipes]);

  // Filter and sort recipes
  const filteredRecipes = useMemo(() => {
    const pinnedIds = new Set(visibleNewlyAddedIds);
    const filtered = recipes.filter(recipe => {
      // Exclude optimistically removed recipes
      if (optimisticallyRemovedRecipes.has(recipe.id)) return false;
      
      // Exclude in-progress remixes (pending or processing)
      if (!isCookbookDisplayable(recipe)) return false;

      // Just-added recipes bypass search and filters so they're findable
      if (pinnedIds.has(recipe.id)) return true;

      // Search filter - search by name, description, and cuisine
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = recipe.name.toLowerCase().includes(query) ||
          recipe.description?.toLowerCase().includes(query) ||
          recipe.cuisine?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Cuisine filter
      if (selectedCuisines.length > 0) {
        if (!recipe.cuisine || !selectedCuisines.includes(recipe.cuisine)) {
          return false;
        }
      }

      // Difficulty filter
      if (selectedDifficulty && recipe.difficulty !== selectedDifficulty) {
        return false;
      }

      // Prep time filter
      if (selectedPrepTime) {
        const prepTime = recipe.prepTimeMinutes;
        switch (selectedPrepTime) {
          case 'quick':
            if (prepTime > 15) return false;
            break;
          case 'medium':
            if (prepTime <= 15 || prepTime > 30) return false;
            break;
          case 'long':
            if (prepTime <= 30) return false;
            break;
        }
      }

      // Total time filter
      if (selectedTotalTime) {
        const totalTime = recipe.totalTimeMinutes;
        switch (selectedTotalTime) {
          case 'quick':
            if (totalTime > 30) return false;
            break;
          case 'medium':
            if (totalTime <= 30 || totalTime > 60) return false;
            break;
          case 'long':
            if (totalTime <= 60) return false;
            break;
        }
      }

      // Dietary restrictions filter
      if (selectedDietaryRestrictions.length > 0) {
        const recipeRestrictions = recipe.dietaryRestrictions || [];
        const hasAllRestrictions = selectedDietaryRestrictions.every(restriction =>
          recipeRestrictions.includes(restriction)
        );
        if (!hasAllRestrictions) return false;
      }

      // Origin filter
      if (selectedOrigin && recipe.origin !== selectedOrigin) {
        return false;
      }

      // Meal filter: slots-first with course fallback (untagged reads as
      // dinner-ish, same as everywhere else). Flag-guarded so a persisted
      // selection can't invisibly hide recipes once the control is gone
      if (
        additionalMealsEnabled &&
        selectedMeal &&
        !MEAL_SLOT_MATCHERS[selectedMeal as keyof typeof MEAL_SLOT_MATCHERS]?.(recipe)
      ) {
        return false;
      }

      // Needs review filter
      if (showNeedsReviewOnly) {
        const isNeedsReview = recipe.importStatus?.status === 'needs_review';
        if (!isNeedsReview) return false;
      }

      return true;
    });

    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'created':
          aValue = new Date(a.createdAt || 0);
          bValue = new Date(b.createdAt || 0);
          break;
        case 'totalTime':
          aValue = a.totalTimeMinutes;
          bValue = b.totalTimeMinutes;
          break;
        case 'difficulty':
          // Sort by difficulty: easy < medium < hard
          const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3 };
          aValue = difficultyOrder[a.difficulty || 'medium'];
          bValue = difficultyOrder[b.difficulty || 'medium'];
          break;
        case 'usage':
          aValue = a.usageCount || 0;
          bValue = b.usageCount || 0;
          break;
        case 'cuisine':
          aValue = (a.cuisine || '').toLowerCase();
          bValue = (b.cuisine || '').toLowerCase();
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return pinRecipesToTop(sorted, visibleNewlyAddedIds);
  }, [recipes, visibleNewlyAddedIds, searchQuery, selectedCuisines, selectedDifficulty, selectedPrepTime, selectedTotalTime, selectedDietaryRestrictions, selectedOrigin, selectedMeal, additionalMealsEnabled, showNeedsReviewOnly, optimisticallyRemovedRecipes, sortField, sortDirection]);

  const undoDeleteRecipe = useCallback(async (recipeId: string) => {
    if (!user) return false;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/id/${recipeId}/undo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to restore recipe');
      }

      return true;
    } catch (error: any) {
      console.error('Error restoring recipe:', error);
      setError(error.message);
      return false;
    }
  }, [user]);

  const handleUndo = async (recipe: Recipe, notificationId: string, action: 'remove' | 'delete') => {
    let success = false;

    if (action === 'remove') {
      // Re-add recipe to cookbook
      success = await addRecipeToCookbook(recipe.id);
    } else if (action === 'delete') {
      // Restore deleted recipe
      success = await undoDeleteRecipe(recipe.id);
    }

    if (success) {
      // Add recipe back to the recipes array
      setRecipes(prev => {
        // Check if recipe already exists to avoid duplicates
        const exists = prev.some(r => r.id === recipe.id);
        return exists ? prev : [...prev, recipe];
      });

      // Remove from optimistically removed set
      setOptimisticallyRemovedRecipes(prev => {
        const newSet = new Set(prev);
        newSet.delete(recipe.id);
        return newSet;
      });
      notifications.hide(notificationId);
    } else {
      notifications.show({
        title: 'Error',
        message: `Failed to restore recipe${action === 'delete' ? '' : ' to cookbook'}`,
        color: 'red',
      });
    }
  };

  const handleRecipeAction = async (recipe: Recipe, action: 'remove' | 'delete') => {
    // Optimistically remove recipe from UI immediately
    setOptimisticallyRemovedRecipes(prev => new Set(prev).add(recipe.id));
    
    const notificationId = `${action}-${recipe.id}`;
    
    try {
      let success = false;
      
      if (action === 'remove') {
        success = await removeRecipeFromCookbook(recipe.id);
      } else if (action === 'delete') {
        success = await deleteRecipe(recipe.id);
      }
      
      if (success) {
        // Remove from local state immediately
        setRecipes(prev => prev.filter(r => r.id !== recipe.id));
        
        // Configure notification based on action
        const config = action === 'remove' 
          ? {
              title: 'Recipe removed from cookbook',
              message: `"${recipe.name}" has been removed from your cookbook`
            }
          : {
              title: 'Recipe deleted',
              message: `"${recipe.name}" has been deleted`
            };
        
        // Create undo button component with loading state
        const UndoButton = () => {
          const [isUndoing, setIsUndoing] = useState(false);

          return (
            <Button
              size="xs"
              variant="subtle"
              color="orange"
              loading={isUndoing}
              onClick={async () => {
                setIsUndoing(true);
                try {
                  await handleUndo(recipe, notificationId, action);
                } finally {
                  setIsUndoing(false);
                }
              }}
            >
              Undo
            </Button>
          );
        };
        
        // Show notification with undo option
        // notificationId already defined above
        notifications.show({
          id: notificationId,
          title: config.title,
          message: (
            <Group>
              <Text size="sm">{config.message}</Text>
              <UndoButton />
            </Group>
          ),
          color: 'orange',
          autoClose: 5000,
          withCloseButton: true,
        });
      } else {
        // If API call failed, restore the recipe and show error
        setOptimisticallyRemovedRecipes(prev => {
          const newSet = new Set(prev);
          newSet.delete(recipe.id);
          return newSet;
        });
        notifications.hide(notificationId);
        notifications.show({
          title: 'Error',
          message: `Failed to ${action === 'remove' ? 'remove recipe from cookbook' : 'delete recipe'}. Please try again.`,
          color: 'red',
        });
      }
    } catch (error) {
      // If API call errored, restore the recipe and show error
      setOptimisticallyRemovedRecipes(prev => {
        const newSet = new Set(prev);
        newSet.delete(recipe.id);
        return newSet;
      });
      notifications.hide(notificationId);
      notifications.show({
        title: 'Error',
        message: `Failed to ${action === 'remove' ? 'remove recipe from cookbook' : 'delete recipe'}. Please try again.`,
        color: 'red',
      });
    }
  };

  const handleRemoveClick = (recipe: Recipe) => {
    handleRecipeAction(recipe, 'remove');
  };

  const handleDeleteClick = (recipe: Recipe) => {
    handleRecipeAction(recipe, 'delete');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCuisines([]);
    setSelectedDifficulty('');
    setSelectedDietaryRestrictions([]);
    setSelectedPrepTime('');
    setSelectedTotalTime('');
    setSelectedOrigin('');
    setSelectedMeal('');
    setShowNeedsReviewOnly(false);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // Remix functionality handlers
  const handleToggleRemixMode = () => {
    setRemixMode(!remixMode);
    setSelectedRecipes(new Set());
  };

  const handleRecipeSelect = (recipeId: string, selected: boolean) => {
    setSelectedRecipes(prev => {
      const newSet = new Set(prev);
      if (selected) {
        // Only add if we haven't reached the limit of 3
        if (newSet.size < 3) {
          newSet.add(recipeId);
        }
      } else {
        newSet.delete(recipeId);
      }
      return newSet;
    });
  };

  const handleRemixMenuClick = (recipe: Recipe) => {
    // Enter remix mode and pre-select this recipe
    setRemixMode(true);
    setSelectedRecipes(new Set([recipe.id]));
  };

  const handleRemixSubmit = async (recipeIds: string[]) => {
    if (recipeIds.length === 0) return;

    setIsRemixing(true);
    try {
      const token = await user?.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/remix`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipeIds: recipeIds,
          servings: 4, // Default servings
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start recipe remix');
      }

      // Reset remix mode and selections
      setRemixMode(false);
      setSelectedRecipes(new Set());

      // Show success notification
      notifications.show({
        title: 'Recipe remix started',
        message: "Your remixed recipe is being created. It'll appear at the top of your cookbook when it's ready.",
        color: 'violet',
        icon: <IconWand size={18} />,
        autoClose: 6000,
      });

      // Refresh the recipes list to show the new remix (it will appear with pending status)
      setTimeout(() => {
        fetchUserRecipes(false); // Refresh without loading spinner
      }, 100);

    } catch (error: any) {
      notifications.show({
        title: 'Remix failed',
        message: error.message || 'Failed to start recipe remix. Please try again.',
        color: 'red',
      });
    } finally {
      setIsRemixing(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
  const startIndex = (currentPage - 1) * recipesPerPage;
  const endIndex = startIndex + recipesPerPage;
  const currentRecipes = filteredRecipes.slice(startIndex, endIndex);

  // Scroll to top when page changes (but not on initial load)
  useEffect(() => {
    if (firstRecipeRef.current && currentRecipes.length > 0 && hasInitiallyLoadedRef.current) {
      // Use requestAnimationFrame for better timing with DOM updates
      requestAnimationFrame(() => {
        firstRecipeRef.current?.scrollIntoView({ 
          behavior: 'auto', 
          block: 'center' // Use center to avoid being cut off by the navbar
        });
      });
    }
    // Mark as initially loaded after first render
    if (currentRecipes.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [currentPage, currentRecipes.length]);

  // Save state to localStorage whenever it changes (including pagination)
  useEffect(() => {
    const currentState = {
      searchQuery,
      selectedCuisines,
      selectedDifficulty,
      selectedDietaryRestrictions,
      selectedPrepTime,
      selectedTotalTime,
      showNeedsReviewOnly,
      selectedOrigin,
      selectedMeal,
      sortField,
      sortDirection,
      currentPage,
    };
    saveCookbookState(currentState);
  }, [searchQuery, selectedCuisines, selectedDifficulty, selectedDietaryRestrictions, selectedPrepTime, selectedTotalTime, showNeedsReviewOnly, selectedOrigin, selectedMeal, sortField, sortDirection, currentPage]);

  // Auto-reset to page 1 when filters change and current page would be invalid
  useEffect(() => {
    const totalPages = Math.ceil(filteredRecipes.length / recipesPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredRecipes.length, currentPage, recipesPerPage]);

  // The banner's Collapse animates closed after dismissal; keep the last
  // non-empty list so the exiting banner doesn't flash an empty message.
  const lastNewlyAddedRef = useRef<Recipe[]>([]);
  const newlyAddedRecipes = useMemo(
    () =>
      visibleNewlyAddedIds
        .map(id => recipes.find(r => r.id === id))
        .filter((r): r is Recipe => !!r),
    [visibleNewlyAddedIds, recipes]
  );
  if (newlyAddedRecipes.length > 0) {
    lastNewlyAddedRef.current = newlyAddedRecipes;
  }
  const bannerRecipes = newlyAddedRecipes.length > 0
    ? newlyAddedRecipes
    : lastNewlyAddedRef.current;

  // "Show me" always gives visible feedback: it scrolls to the pinned cards
  // when they're off-screen and pulses a ring around them either way, so on
  // a desktop where they're already in view the button doesn't feel dead.
  const resultsGridRef = useRef<HTMLDivElement>(null);
  const [showNewlyAddedRequest, setShowNewlyAddedRequest] = useState(0);
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const handleShowNewlyAdded = () => {
    // Pinned cards always lead page 1
    setCurrentPage(1);
    setShowNewlyAddedRequest(n => n + 1);
  };

  useEffect(() => {
    if (showNewlyAddedRequest === 0) return;
    // Wait a frame so a page switch to page 1 has rendered
    const frame = requestAnimationFrame(() => {
      const cards = Array.from(
        resultsGridRef.current?.querySelectorAll<HTMLElement>('[data-newly-added]') ?? []
      );
      if (cards.length === 0) return;
      const rect = cards[0].getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!inView) {
        cards[0].scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });
      }
      // Green ring (Mantine green-6); reduced motion fades a fixed ring
      // instead of expanding one
      const keyframes = prefersReducedMotion
        ? [
            { boxShadow: '0 0 0 4px rgba(64, 192, 87, 0.6)' },
            { boxShadow: '0 0 0 4px rgba(64, 192, 87, 0)' },
          ]
        : [
            { boxShadow: '0 0 0 0 rgba(64, 192, 87, 0.6)' },
            { boxShadow: '0 0 0 10px rgba(64, 192, 87, 0)' },
          ];
      cards.forEach(card =>
        card.animate?.(keyframes, {
          duration: 900,
          iterations: 2,
          easing: 'ease-out',
          // Let a smooth scroll land first so the pulse is seen
          delay: inView || prefersReducedMotion ? 0 : 400,
        })
      );
    });
    return () => cancelAnimationFrame(frame);
    // Only a new request should pulse, not a reduced-motion change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewlyAddedRequest]);

  const handleRecipeClick = (recipe: Recipe) => {
    if (!user) return;
    navigate(buildRecipePath(recipe, effectiveUid!), { state: { recipe } });
  };

  const handleImportStarted = useCallback(() => {
    // Close the modal and refresh recipes to show any new recipes
    setImportModalOpened(false);
    
    // Add a small delay to ensure database transaction has committed
    setTimeout(() => {
      fetchUserRecipes(false); // Refresh without loading spinner
    }, 100);
  }, [fetchUserRecipes]);

  const handleSaveNeedsReviewClick = useCallback(async (recipe: Recipe) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipe.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save recipe');
      }

      // Update the recipe in the local state to reflect it's no longer in
      // needs_review
      setRecipes(prev => 
        prev.map(r => 
          r.id === recipe.id 
            ? { ...r, importStatus: undefined, isSaved: true }
            : r
        )
      );

      notifications.show({
        title: 'Recipe saved',
        message: 'Recipe has been added to your cookbook',
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 6000,
      });

    } catch (error: any) {
      notifications.show({
        title: 'Save failed',
        message: error.message || 'Failed to save recipe. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [user]);

  const handleCreateManualRecipe = useCallback(() => {
    // Navigate to recipe details page in create mode
    if (user) {
      navigate(`/u/${effectiveUid}/recipe/new?create=true`);
    }
  }, [navigate, user, effectiveUid]);

  const fetchFailedRecipes = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/recipes/failed`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch failed recipes');
      }

      const data = await response.json();
      setFailedRecipes(data.recipes || []);
    } catch (error: any) {
      console.error('Error fetching failed recipes:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load failed imports. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 10000,
      });
    }
  }, [user]);

  const handleViewFailedImports = useCallback(async () => {
    await fetchFailedRecipes();
    setFailedImportsModalOpened(true);
  }, [fetchFailedRecipes]);

  return (
    <MobileOptimized>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ActionIcon 
              variant="subtle" 
              onClick={onBack}
              size="lg"
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={0} className="page-header">
              <Title order={2}>My cookbook</Title>
              {/* div (not p) so the loading skeleton can sit inside, keeping
                  the subtitle's line height instead of flashing "0 recipes" */}
              <Text component="div" c="dimmed" size="sm" className="page-subtitle">
                {loading ? (
                  <Skeleton height={12} width={170} display="inline-block" style={{ verticalAlign: 'middle' }} />
                ) : (
                  <>{formatCount(recipes.length)} recipe{recipes.length !== 1 ? 's' : ''} in your collection</>
                )}
              </Text>
            </Stack>
          </Group>
          
          {/* Import/Create button - desktop: split button */}
          <Group visibleFrom="sm">
            <Button
              variant="subtle"
              leftSection={<IconArchive size={16} />}
              onClick={() => navigate('/archive')}
            >
              Recipe archive
            </Button>
            <SplitButton
              mainAction={{
                label: "Import recipes",
                onClick: () => setImportModalOpened(true),
                leftSection: <IconPhoto size={16} />
              }}
              menuItems={[
                {
                  label: "Create recipe",
                  onClick: handleCreateManualRecipe,
                  leftSection: <IconPlus size={16} stroke={1.5} />
                },
                {
                  label: remixMode ? "Cancel remix" : "Remix recipes",
                  onClick: handleToggleRemixMode,
                  leftSection: remixMode ? <IconX size={16} stroke={1.5} /> : <IconWand size={16} stroke={1.5} />,
                  disabled: currentRecipes.length === 0
                }
              ]}
            />
          </Group>
          
          {/* Mobile: Single menu with all options */}
          <Group hiddenFrom="sm">
            <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="orange"
                  size="lg"
                >
                  <IconDotsVertical size={20} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconArchive size={18} stroke={1.5} />}
                  onClick={() => navigate('/archive')}
                  style={{ fontSize: '16px', padding: '12px 16px' }}
                >
                  Recipe archive
                </Menu.Item>
                <Menu.Divider />
{remixMode && (
                  <>
                    <Menu.Item
                      leftSection={<IconX size={18} stroke={1.5} />}
                      onClick={handleToggleRemixMode}
                      color="red"
                      style={{ fontSize: '16px', padding: '12px 16px' }}
                    >
                      Cancel remix
                    </Menu.Item>
                    <Menu.Divider />
                  </>
                )}
                <Menu.Label>Add recipes</Menu.Label>
                <Menu.Item
                  leftSection={<IconPhoto size={18} stroke={1.5} />}
                  onClick={() => setImportModalOpened(true)}
                  style={{ fontSize: '16px', padding: '12px 16px' }}
                >
                  Import recipes
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconPlus size={18} stroke={1.5} />}
                  onClick={handleCreateManualRecipe}
                  style={{ fontSize: '16px', padding: '12px 16px' }}
                >
                  Create recipe
                </Menu.Item>
                {!remixMode && (
                  <Menu.Item
                    leftSection={<IconWand size={18} stroke={1.5} />}
                    onClick={handleToggleRemixMode}
                    disabled={currentRecipes.length === 0}
                    style={{ fontSize: '16px', padding: '12px 16px' }}
                  >
                    Remix recipes
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} color="red" />} color="red" radius="lg">
            <Text size="sm" c="red.7">{error}</Text>
          </Alert>
        )}

        {/* Active Import Notification */}
        <Collapse in={hasActiveImports}>
          <Alert icon={<Loader size={16} color="blue" />} color="blue" variant="light" radius="lg">
            <Text size="sm" c="blue.7">
              {(() => {
                const total =
                  importCounts.pending + importCounts.processing ||
                  lastImportTotalRef.current;
                return total === 1
                  ? "Recipe is importing... It'll appear at the top of your cookbook when it's ready."
                  : `${formatCount(total)} recipes are importing... They'll appear at the top of your cookbook when they're ready.`;
              })()}
            </Text>
          </Alert>
        </Collapse>

        {/* Active Remix Notification */}
        <Collapse in={hasActiveRemixes}>
          <Alert icon={<Loader size={16} color="violet" />} color="violet" variant="light" radius="lg">
            <Text size="sm" c="violet.7">
              {(() => {
                const total =
                  remixCounts.pending + remixCounts.processing ||
                  lastRemixTotalRef.current;
                return total === 1
                  ? "Recipe remix is in progress... It'll appear at the top of your cookbook when it's ready."
                  : `${formatCount(total)} recipe remixes are in progress... They'll appear at the top of your cookbook when they're ready.`;
              })()}
            </Text>
          </Alert>
        </Collapse>

        {/* Failed Import Notification */}
        <Collapse in={importCounts.failed > 0}>
          <Alert color="orange" variant="light" radius="lg">
            <Group gap="sm" align="center" style={{ minHeight: 20 }}>
              <IconAlertCircle size={16} color="var(--mantine-color-orange-6)" />
              <Text size="sm" c="orange.7" style={{ flex: 1 }}>
                {formatCount(displayFailedCount)} recipe import{displayFailedCount !== 1 ? 's' : ''} failed
              </Text>
              <Button
                size="xs"
                variant="subtle"
                color="orange"
                onClick={handleViewFailedImports}
              >
                View Details
              </Button>
            </Group>
          </Alert>
        </Collapse>

        {/* Just-added Notification */}
        <Collapse in={newlyAddedRecipes.length > 0}>
          <Alert color="green" variant="light" radius="lg">
            {/* Phones: actions on their own row so the message keeps the
                full width; wider screens: actions inline on the right */}
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              align={{ base: 'stretch', sm: 'center' }}
              gap={{ base: 4, sm: 'sm' }}
            >
              <Group gap="sm" align="flex-start" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                <IconCheck size={16} color="var(--mantine-color-green-6)" style={{ flexShrink: 0, marginTop: 2 }} />
                <Text size="sm" c="green.8" style={{ flex: 1, minWidth: 0 }}>
                  {bannerRecipes.length > 0 && describeNewlyAdded(bannerRecipes)}
                </Text>
              </Group>
              {/* On phones, pull the subtle buttons left so their labels line
                  up with the message text (icon 16px + gap 12px - button padding) */}
              <Group gap={4} wrap="nowrap" ml={{ base: 14, sm: 0 }} style={{ flexShrink: 0 }}>
                <Button size="xs" variant="subtle" color="green" onClick={handleShowNewlyAdded}>
                  Show me
                </Button>
                <Button size="xs" variant="subtle" color="gray" onClick={() => setNewlyAddedIds([])}>
                  Dismiss
                </Button>
              </Group>
            </Flex>
          </Alert>
        </Collapse>

        {/* Search and Filters */}
        <Paper p="md" radius="lg" withBorder>
          <RecipeSearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedCuisines={selectedCuisines}
            selectedDifficulty={selectedDifficulty}
            selectedDietaryRestrictions={selectedDietaryRestrictions}
            selectedPrepTime={selectedPrepTime}
            selectedTotalTime={selectedTotalTime}
            selectedOrigin={selectedOrigin}
            showNeedsReviewOnly={showNeedsReviewOnly}
            cuisineOptions={cuisineOptions}
            dietaryOptions={dietaryOptions}
            onSelectedCuisinesChange={setSelectedCuisines}
            onSelectedDifficultyChange={setSelectedDifficulty}
            onSelectedDietaryRestrictionsChange={setSelectedDietaryRestrictions}
            onSelectedPrepTimeChange={setSelectedPrepTime}
            onSelectedTotalTimeChange={setSelectedTotalTime}
            onSelectedOriginChange={setSelectedOrigin}
            selectedMeal={selectedMeal}
            onSelectedMealChange={additionalMealsEnabled ? setSelectedMeal : undefined}
            onShowNeedsReviewOnlyChange={setShowNeedsReviewOnly}
            onClearFilters={clearFilters}
            showCollapsible={true}
            showNeedsReviewFilter={true}
            showClearAllButton={true}
            defaultExpanded={false}
            searchPlaceholder="Search recipes by name, description, or cuisine..."
          />
        </Paper>

        {/* Sort Options */}
        <Group justify="space-between" align="center">
          {loading ? (
            <Skeleton height={20} width="min(280px, 85%)" />
          ) : (
            <Text fw={500}>
              {formatCount(filteredRecipes.length)} recipe{filteredRecipes.length !== 1 ? 's' : ''} found
              {filteredRecipes.length > recipesPerPage && (
                <Text component="span" c="dimmed" size="sm">
                  {' '}• Showing {formatCount(startIndex + 1)}-{formatCount(Math.min(endIndex, filteredRecipes.length))} of {formatCount(filteredRecipes.length)}
                </Text>
              )}
            </Text>
          )}
          {loading ? (
            <Group gap="xs">
              <Skeleton height={36} width={140} radius="md" />
              <Skeleton height={28} width={28} radius="md" />
            </Group>
          ) : (
            <Group gap="xs">
              <Select
                placeholder="Sort by"
                value={sortField}
                onChange={(value) => setSortField(value || 'name')}
                data={[
                  { value: 'name', label: 'Name' },
                  { value: 'created', label: 'Date added' },
                  { value: 'totalTime', label: 'Total time' },
                  { value: 'difficulty', label: 'Difficulty' },
                  { value: 'usage', label: 'Usage count' },
                  { value: 'cuisine', label: 'Cuisine' }
                ]}
                size="sm"
                w={140}
              />
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                {sortDirection === 'asc' ? <IconArrowUp size={16} /> : <IconArrowDown size={16} />}
              </ActionIcon>
            </Group>
          )}
        </Group>

        {/* Remix Mode Instructions */}
        {remixMode && (
          <Alert color="violet" variant="light" radius="lg">
            {/* Mobile: Layout with cancel button on same line as text, always-visible remix button */}
            <Stack gap="sm" hiddenFrom="sm">
              <Group justify="space-between" align="center">
                <Text size="sm" c="violet.7" style={{ flex: 1 }}>
                  Select 1-3 recipes to remix
                </Text>
                <Button
                  size="xs"
                  variant="subtle"
                  color="violet"
                  leftSection={<IconX size={14} />}
                  onClick={handleToggleRemixMode}
                >
                  Cancel
                </Button>
              </Group>
              <Button
                size="sm"
                variant={selectedRecipes.size === 0 ? "outline" : "filled"}
                color={selectedRecipes.size === 0 ? "gray" : "violet"}
                leftSection={<IconWand size={16} />}
                loading={isRemixing}
                disabled={selectedRecipes.size === 0}
                onClick={() => handleRemixSubmit(Array.from(selectedRecipes))}
                fullWidth
                styles={selectedRecipes.size === 0 ? {
                  root: {
                    borderColor: 'var(--mantine-color-gray-4)',
                    color: 'var(--mantine-color-gray-6)',
                    backgroundColor: 'transparent',
                    '&:disabled': {
                      borderColor: 'var(--mantine-color-gray-4)',
                      color: 'var(--mantine-color-gray-6)',
                      backgroundColor: 'transparent',
                    }
                  }
                } : undefined}
              >
                {selectedRecipes.size === 0 
                  ? "Select recipes to remix"
                  : `Remix ${selectedRecipes.size} Recipe${selectedRecipes.size !== 1 ? 's' : ''}`
                }
              </Button>
            </Stack>
            
            {/* Desktop: Horizontal layout */}
            <Group justify="space-between" align="center" visibleFrom="sm">
              <Text size="sm" style={{ flex: 1 }} c="violet.7">
                Select 1-3 recipes to remix
              </Text>
              <Group gap="xs">
                {selectedRecipes.size > 0 && (
                  <Button
                    size="xs"
                    variant="filled"
                    color="violet"
                    leftSection={<IconWand size={14} />}
                    loading={isRemixing}
                    onClick={() => handleRemixSubmit(Array.from(selectedRecipes))}
                  >
                    Remix {selectedRecipes.size} Recipe{selectedRecipes.size !== 1 ? 's' : ''}
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="subtle"
                  color="violet"
                  leftSection={<IconX size={14} />}
                  onClick={handleToggleRemixMode}
                >
                  Cancel
                </Button>
              </Group>
            </Group>
          </Alert>
        )}

        {/* Results */}
        <Stack gap="md">

          {loading ? (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} p="md" radius="lg" withBorder>
                  <Flex gap={{ base: 'sm', sm: 'md' }}>
                    <Skeleton w={{ base: 80, sm: 120 }} h={{ base: 80, sm: 120 }} radius="md" style={{ flexShrink: 0 }} />
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Skeleton height={20} width="70%" />
                      <Skeleton height={14} width="100%" />
                      <Skeleton height={14} width="80%" />
                      {/* Time + servings + difficulty share a line on tablet+ */}
                      <Group gap="xs">
                        <Skeleton height={14} width={80} />
                        <Skeleton height={14} width={80} />
                        <Skeleton height={18} width={50} radius="xl" />
                      </Group>
                      {/* Cuisine + dietary/tag badges */}
                      <Group gap="xs">
                        <Skeleton height={18} width={60} radius="xl" />
                        <Skeleton height={18} width={70} radius="xl" />
                        <Skeleton height={18} width={80} radius="xl" />
                      </Group>
                    </Stack>
                  </Flex>
                </Card>
              ))}
            </SimpleGrid>
          ) : filteredRecipes.length === 0 ? (
            <Paper p="xl" radius="lg" withBorder ta="center">
              <Stack gap="md" align="center">
                <IconBookmark size={48} color="var(--mantine-color-gray-5)" />
                <Stack gap="xs" align="center">
                  <Text fw={500}>No recipes found</Text>
                  <Text c="dimmed" size="sm">
                    {recipes.length === 0 
                      ? "You haven't saved any recipes to your cookbook yet."
                      : "Try adjusting your search or filters."
                    }
                  </Text>
                </Stack>
              </Stack>
            </Paper>
          ) : (
            <>
              <SimpleGrid 
                ref={resultsGridRef}
                cols={{ base: 1, md: 2 }}
                spacing="md"
              >
                {currentRecipes.map((recipe, index) => (
                  <Card
                    key={recipe.id}
                    ref={index === 0 ? firstRecipeRef : undefined}
                    data-newly-added={newlyAddedIds.includes(recipe.id) || undefined}
                    p="md"
                    radius="lg"
                    withBorder
                    style={{
                      transition: 'all 0.2s ease',
                      cursor: remixMode && recipe.isSaved
                        ? (!selectedRecipes.has(recipe.id) && selectedRecipes.size >= 3 ? 'not-allowed' : 'pointer')
                        : undefined,
                      backgroundColor: remixMode && selectedRecipes.has(recipe.id) 
                        ? 'var(--mantine-color-violet-0)' 
                        : undefined,
                      borderColor: remixMode && selectedRecipes.has(recipe.id) 
                        ? 'var(--mantine-color-violet-3)' 
                        : newlyAddedIds.includes(recipe.id)
                          ? 'var(--mantine-color-green-4)'
                          : undefined
                    }}
                    onClick={remixMode && recipe.isSaved 
                      ? () => handleRecipeSelect(recipe.id, !selectedRecipes.has(recipe.id))
                      : undefined
                    }
                  >
                    <Flex gap={{ base: 'sm', sm: 'md' }}>
                      {/* Recipe Image and Status */}
                      <Stack gap="xs" align="center">
                        <Box 
                          w={{ base: 80, sm: 120 }}
                          h={{ base: 80, sm: 120 }}
                          style={{ 
                            flexShrink: 0,
                            borderRadius: 'var(--mantine-radius-md)',
                            overflow: 'hidden',
                            backgroundColor: 'var(--mantine-color-gray-1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            cursor: remixMode ? 'inherit' : 'pointer'
                          }}
                          onClick={remixMode ? undefined : () => handleRecipeClick(recipe)}
                        >
                          {/* Recipe Selection Indicator (overlaid on image in remix mode) */}
                          {remixMode && recipe.isSaved && (
                            <ActionIcon
                              style={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 10,
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                opacity: selectedRecipes.has(recipe.id) ? 0.9 : 0.8
                              }}
                              variant={selectedRecipes.has(recipe.id) ? "filled" : "white"}
                              color={selectedRecipes.has(recipe.id) ? "violet" : "gray"}
                              size="md"
                              radius="xl"
                              disabled={!selectedRecipes.has(recipe.id) && selectedRecipes.size >= 3}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRecipeSelect(recipe.id, !selectedRecipes.has(recipe.id));
                              }}
                            >
                              {selectedRecipes.has(recipe.id) && <IconCheck size={16} />}
                            </ActionIcon>
                          )}
                          <RecipeImage
                            src={recipe.imageUrl}
                            alt={recipe.name}
                            width="100%"
                            height="100%"
                            fallbackIconSize={32}
                          />
                          {/* No Top pick ribbon here: everything in the cookbook is saved, so top picks have graduated */}
                        </Box>
                        {/* Status Badge below image */}
                        {recipe.importStatus?.status === 'needs_review' && (
                          <Tooltip
                            label="This recipe was imported but needs manual review and editing to ensure accuracy before saving"
                            multiline
                            w={220}
                            withArrow
                            events={{ hover: true, focus: true, touch: true }}
                          >
                            <Badge size="xs" variant="light" color="orange" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                              Needs Review
                            </Badge>
                          </Tooltip>
                        )}
                      </Stack>
                      
                      {/* Recipe Content */}
                      <Stack gap="xs" style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                        <Group gap="xs" align="flex-start" style={{ paddingRight: 40 }}>
                          {/* md at base: beside the 80px thumbnail a ~120px
                              title column at lg wraps one word per line */}
                          <Text
                            fw={500}
                            fz={{ base: 'md', sm: 'lg' }}
                            style={{
                              cursor: remixMode ? 'inherit' : 'pointer'
                            }}
                            onClick={remixMode ? undefined : () => handleRecipeClick(recipe)}
                          >
                            {recipe.name}
                          </Text>
                          {newlyAddedIds.includes(recipe.id) && (
                            // One title-line tall box (same font size as the
                            // title) so the badge centers on the first line
                            // instead of hugging the top of the row
                            <Box
                              fz={{ base: 'md', sm: 'lg' }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                height: 'calc(1em * var(--mantine-line-height))',
                              }}
                            >
                              <Badge size="sm" variant="filled" color="green" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                New
                              </Badge>
                            </Box>
                          )}
                        </Group>
                        <Menu shadow="md" width={220}>
                          <Menu.Target>
                            <ActionIcon 
                              variant="subtle" 
                              size="lg" 
                              style={{ position: 'absolute', top: -4, right: -4 }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <IconDotsVertical size={20} />
                            </ActionIcon>
                          </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                                onClick={() => {
                                  if (user) {
                                    navigate(`${buildRecipePath(recipe, effectiveUid!)}?edit=true`, { state: { recipe } });
                                  }
                                }}
                                style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                              >
                                Edit Recipe
                              </Menu.Item>
                              {recipe.isSaved && (
                                <Menu.Item
                                  leftSection={<IconWand style={{ width: rem(14), height: rem(14) }} />}
                                  onClick={() => handleRemixMenuClick(recipe)}
                                  style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                >
                                  Remix recipe
                                </Menu.Item>
                              )}
                              {recipe.importStatus?.status === 'needs_review' ? (
                                <>
                                  <Menu.Item
                                    leftSection={<IconCheck style={{ width: rem(14), height: rem(14) }} />}
                                    color="green"
                                    onClick={() => handleSaveNeedsReviewClick(recipe)}
                                    style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                  >
                                    Confirm Recipe
                                  </Menu.Item>
                                  <Menu.Item
                                    leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                                    color="orange"
                                    onClick={() => handleDeleteClick(recipe)}
                                    style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                  >
                                    Delete recipe
                                  </Menu.Item>
                                </>
                              ) : (
                                <>
                                  <Menu.Item
                                    leftSection={<IconBookmarkOff style={{ width: rem(14), height: rem(14) }} />}
                                    color="orange"
                                    onClick={() => handleRemoveClick(recipe)}
                                    style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                  >
                                    Remove from cookbook
                                  </Menu.Item>
                                  <Menu.Item
                                    leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                                    color="red"
                                    onClick={() => handleDeleteClick(recipe)}
                                    style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                                  >
                                    Delete recipe
                                  </Menu.Item>
                                </>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        
                        {recipe.description && (
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            {recipe.description}
                          </Text>
                        )}
                        
                        {/* Mobile: Time and servings on own line */}
                        <Group gap="md" hiddenFrom="sm">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
                          </Group>
                        </Group>

                        {/* Tablet+: Time, servings, and functional badges on same line */}
                        <Group gap="xs" visibleFrom="sm">
                          <Group gap="xs">
                            <IconClock size={16} />
                            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
                          </Group>
                          <Group gap="xs">
                            <IconUsers size={16} />
                            <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
                          </Group>
                          {recipe.difficulty && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                              style={{ cursor: remixMode ? 'inherit' : 'default' }}
                            >
                              {recipe.difficulty}
                            </Badge>
                          )}
                          {recipe.usageCount && recipe.usageCount > 0 && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color="blue"
                              style={{ cursor: remixMode ? 'inherit' : 'default' }}
                            >
                              Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          <RecipeProvenanceBadges
                            recipe={recipe}
                            badgeStyle={{ cursor: remixMode ? 'inherit' : 'default' }}
                          />
                        </Group>
                        
                        {/* Mobile: All badges on single wrapping line */}
                        <Group gap="xs" hiddenFrom="sm">
                          {recipe.difficulty && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
                              style={{ cursor: remixMode ? 'inherit' : 'default' }}
                            >
                              {recipe.difficulty}
                            </Badge>
                          )}
                          {recipe.usageCount && recipe.usageCount > 0 && (
                            <Badge 
                              size="sm" 
                              variant="light"
                              color="blue"
                              style={{ cursor: remixMode ? 'inherit' : 'default' }}
                            >
                              Used {recipe.usageCount} time{recipe.usageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          <RecipeProvenanceBadges
                            recipe={recipe}
                            badgeStyle={{ cursor: remixMode ? 'inherit' : 'default' }}
                          />
                          {recipe.cuisine && (
                            <CuisineBadge cuisine={recipe.cuisine} style={{ cursor: remixMode ? 'inherit' : 'default' }} />
                          )}
                          {(() => {
                            const allBadges = getRecipeBadges(recipe, badgeViewer);
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 1);
                            
                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu
                                  restrictions={hiddenRestrictions}
                                  style={{ cursor: remixMode ? 'inherit' : 'pointer' }}
                                />
                              </>
                            );
                          })()}
                        </Group>
                        
                        {/* Tablet+: Descriptive badges on second line */}
                        <Group gap="xs" visibleFrom="sm">
                          {recipe.cuisine && (
                            <CuisineBadge cuisine={recipe.cuisine} style={{ cursor: remixMode ? 'inherit' : 'default' }} />
                          )}
                          {(() => {
                            const allBadges = getRecipeBadges(recipe, badgeViewer);
                            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);
                            
                            return (
                              <>
                                {visible.map((badge) => (
                                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline" style={{ cursor: remixMode ? 'inherit' : 'default' }}>
                                    {badge.text}
                                  </Badge>
                                ))}
                                <MoreRestrictionsMenu
                                  restrictions={hiddenRestrictions}
                                  style={{ cursor: remixMode ? 'inherit' : 'pointer' }}
                                />
                              </>
                            );
                          })()}
                        </Group>
                      </Stack>
                    </Flex>
                  </Card>
                ))}
              </SimpleGrid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Group justify="center" mt="xl" mb="md">
                  <Pagination
                    total={totalPages}
                    value={currentPage}
                    onChange={setCurrentPage}
                    size="md"
                    radius="md"
                    color="orange"
                    siblings={0}
                  />
                </Group>
              )}
            </>
          )}
        </Stack>
      </Stack>

      {/* Import Recipe Modal */}
      <ImportRecipeModal
        opened={importModalOpened}
        onClose={() => setImportModalOpened(false)}
        onImportStarted={handleImportStarted}
      />

      {/* Failed Imports Drawer */}
      <FailedImportsDrawer
        opened={failedImportsModalOpened}
        onClose={() => setFailedImportsModalOpened(false)}
        failedRecipes={failedRecipes}
        onRecipesUpdated={() => {
          fetchUserRecipes(false); // Refresh main recipes list
          fetchFailedRecipes(); // Refresh failed recipes list
        }}
      />

    </MobileOptimized>
  );
};
